import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import FileUploadResult from "../classes/FileUploadResult";
import Button from "./Button";
import PlatformClient from "../classes/PlatformClient";
import Browser from "../classes/Browser";
import FileToUpload from "../classes/FileToUpload";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import Admin from "../models/Admin";
import Result from "../classes/Result";
import IconView from "./IconView";
import Divider from "./Divider";
import { BuildContext } from "../classes/BuildContext";

type _AdminSideMenuViewOnMenuChanged = (menu: string) => void;

type _EditButtonOnPressed = (d3eState: AdminSideMenuViewRefs) => void;

type _DashboardButtonOnPressed = (d3eState: AdminSideMenuViewRefs) => void;

type _BusinessManagementButtonOnPressed = (
  d3eState: AdminSideMenuViewRefs
) => void;

type _CategoryManagementButtonOnPressed = (
  d3eState: AdminSideMenuViewRefs
) => void;

type _ConfigurationButtonOnPressed = (d3eState: AdminSideMenuViewRefs) => void;

export interface AdminSideMenuViewProps extends BaseUIProps {
  key?: string;
  admin: Admin;
  onMenuChanged?: _AdminSideMenuViewOnMenuChanged;
}
/// To store state data for AdminSideMenuView
class AdminSideMenuViewRefs {
  public businessManagementButton: BusinessManagementButtonState =
    new BusinessManagementButtonState();
  public button: ButtonState = new ButtonState();
  public categoryManagementButton: CategoryManagementButtonState =
    new CategoryManagementButtonState();
  public configurationButton: ConfigurationButtonState =
    new ConfigurationButtonState();
  public dashboardButton: DashboardButtonState = new DashboardButtonState();
  public defaultImageView: DefaultImageViewState = new DefaultImageViewState();
  public editButton: EditButtonState = new EditButtonState();
  public providerImage: ProviderImageState = new ProviderImageState();
}

interface ConfigurationButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _configurationButtonHandler?: _ConfigurationButtonOnPressed;
}

class ConfigurationButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _ConfigurationButtonWithState extends ObservableComponent<ConfigurationButtonWithStateProps> {
  configurationButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ConfigurationButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get configurationButton(): ConfigurationButtonState {
    return this.props.d3eState.configurationButton;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _configurationButtonHandler(): _ConfigurationButtonOnPressed {
    return this.props._configurationButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "configurationButton",
      null,
      this.configurationButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "configurationButton",
        "configurationButton.",
        "configurationButton.hover",
      ],
      this.rebuild
    );
  }
  public configurationButtonOnEnter(event): void {
    return this.configurationButton.setHover(true);
  }
  public configurationButtonOnExit(event): void {
    return this.configurationButton.setHover(false);
  }
  public dispose(): void {
    this.configurationButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 5.0,
        transitions: new Map(),
      }),
      width: 220,
      child: Button({
        padding: this.configurationButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.configurationButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.configurationButton.disable,
        onPressed: () => {
          this._configurationButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            IconView({
              size: 24,
              icon: MaterialIcons.dashboard,
              color: cStyle.c5,
              className: "x081",
              key: "0",
            }),
            TextView({
              data: "Configuration",
              softWrap: true,
              className: "xae",
              key: "1",
            }),
          ],
          className: "x7e hc h",
        }),
        onEnter: (event) => {
          this.configurationButtonOnEnter(event);
        },
        onExit: (event) => {
          this.configurationButtonOnExit(event);
        },
      }),
      className: "xa8e hc",
    });
  }
}
function ConfigurationButtonWithState(
  props: ConfigurationButtonWithStateProps
) {
  return React.createElement(_ConfigurationButtonWithState, props);
}

interface CategoryManagementButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _categoryManagementButtonHandler?: _CategoryManagementButtonOnPressed;
}

class CategoryManagementButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CategoryManagementButtonWithState extends ObservableComponent<CategoryManagementButtonWithStateProps> {
  categoryManagementButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CategoryManagementButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get categoryManagementButton(): CategoryManagementButtonState {
    return this.props.d3eState.categoryManagementButton;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _categoryManagementButtonHandler(): _CategoryManagementButtonOnPressed {
    return this.props._categoryManagementButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "categoryManagementButton",
      null,
      this.categoryManagementButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "categoryManagementButton",
        "categoryManagementButton.",
        "categoryManagementButton.hover",
      ],
      this.rebuild
    );
  }
  public categoryManagementButtonOnEnter(event): void {
    return this.categoryManagementButton.setHover(true);
  }
  public categoryManagementButtonOnExit(event): void {
    return this.categoryManagementButton.setHover(false);
  }
  public dispose(): void {
    this.categoryManagementButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 5.0,
        transitions: new Map(),
      }),
      width: 220,
      child: Button({
        padding: this.categoryManagementButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.categoryManagementButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.categoryManagementButton.disable,
        onPressed: () => {
          this._categoryManagementButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            IconView({
              size: 24,
              icon: MaterialIcons.dashboard,
              color: cStyle.c5,
              className: "x01e",
              key: "0",
            }),
            TextView({
              data: "Category Management",
              softWrap: true,
              className: "xddb",
              key: "1",
            }),
          ],
          className: "x5c9b hc h",
        }),
        onEnter: (event) => {
          this.categoryManagementButtonOnEnter(event);
        },
        onExit: (event) => {
          this.categoryManagementButtonOnExit(event);
        },
      }),
      className: "x75 hc",
    });
  }
}
function CategoryManagementButtonWithState(
  props: CategoryManagementButtonWithStateProps
) {
  return React.createElement(_CategoryManagementButtonWithState, props);
}

interface BusinessManagementButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _businessManagementButtonHandler?: _BusinessManagementButtonOnPressed;
}

class BusinessManagementButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _BusinessManagementButtonWithState extends ObservableComponent<BusinessManagementButtonWithStateProps> {
  businessManagementButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BusinessManagementButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get businessManagementButton(): BusinessManagementButtonState {
    return this.props.d3eState.businessManagementButton;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _businessManagementButtonHandler(): _BusinessManagementButtonOnPressed {
    return this.props._businessManagementButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "businessManagementButton",
      null,
      this.businessManagementButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "businessManagementButton",
        "businessManagementButton.",
        "businessManagementButton.hover",
      ],
      this.rebuild
    );
  }
  public businessManagementButtonOnEnter(event): void {
    return this.businessManagementButton.setHover(true);
  }
  public businessManagementButtonOnExit(event): void {
    return this.businessManagementButton.setHover(false);
  }
  public dispose(): void {
    this.businessManagementButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 5.0,
        transitions: new Map(),
      }),
      width: 220,
      child: Button({
        padding: this.businessManagementButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.businessManagementButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.businessManagementButton.disable,
        onPressed: () => {
          this._businessManagementButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            IconView({
              size: 24,
              icon: MaterialIcons.dashboard,
              color: cStyle.c5,
              className: "xc2",
              key: "0",
            }),
            TextView({
              data: "Business Management",
              softWrap: true,
              className: "x3c9",
              key: "1",
            }),
          ],
          className: "x5bf hc h",
        }),
        onEnter: (event) => {
          this.businessManagementButtonOnEnter(event);
        },
        onExit: (event) => {
          this.businessManagementButtonOnExit(event);
        },
      }),
      className: "xc762 hc",
    });
  }
}
function BusinessManagementButtonWithState(
  props: BusinessManagementButtonWithStateProps
) {
  return React.createElement(_BusinessManagementButtonWithState, props);
}

interface DashboardButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _dashboardButtonHandler?: _DashboardButtonOnPressed;
}

class DashboardButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _DashboardButtonWithState extends ObservableComponent<DashboardButtonWithStateProps> {
  dashboardButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DashboardButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get dashboardButton(): DashboardButtonState {
    return this.props.d3eState.dashboardButton;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _dashboardButtonHandler(): _DashboardButtonOnPressed {
    return this.props._dashboardButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("dashboardButton", null, this.dashboardButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["dashboardButton", "dashboardButton.", "dashboardButton.hover"],
      this.rebuild
    );
  }
  public dashboardButtonOnEnter(event): void {
    return this.dashboardButton.setHover(true);
  }
  public dashboardButtonOnExit(event): void {
    return this.dashboardButton.setHover(false);
  }
  public dispose(): void {
    this.dashboardButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 5.0, new Map()),
      width: 220,
      child: Button({
        padding: this.dashboardButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.dashboardButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.dashboardButton.disable,
        onPressed: () => {
          this._dashboardButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            IconView({
              size: 24,
              icon: MaterialIcons.dashboard,
              color: cStyle.c5,
              className: "x208",
              key: "0",
            }),
            TextView({
              data: "Dashboard",
              softWrap: true,
              className: "x624",
              key: "1",
            }),
          ],
          className: "xcf hc h",
        }),
        onEnter: (event) => {
          this.dashboardButtonOnEnter(event);
        },
        onExit: (event) => {
          this.dashboardButtonOnExit(event);
        },
      }),
      className: "xda hc",
    });
  }
}
function DashboardButtonWithState(props: DashboardButtonWithStateProps) {
  return React.createElement(_DashboardButtonWithState, props);
}

interface EditButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _onPresseProfileButtonHandler?: _EditButtonOnPressed;
}

class EditButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _EditButtonWithState extends ObservableComponent<EditButtonWithStateProps> {
  editButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: EditButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get editButton(): EditButtonState {
    return this.props.d3eState.editButton;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onPresseProfileButtonHandler(): _EditButtonOnPressed {
    return this.props._onPresseProfileButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("editButton", null, this.editButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["editButton", "editButton.hover"], this.rebuild);
  }
  public editButtonOnEnter(event): void {
    return this.editButton.setHover(true);
  }
  public editButtonOnExit(event): void {
    return this.editButton.setHover(false);
  }
  public dispose(): void {
    this.editButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration: new ui.BoxDecoration({
        color: cStyle.c1,
        border: ui.Border.all({ color: cStyle.c5, width: 2 }),
        borderRadius: ui.BorderRadius.circular(20.0),
        boxShadow: [
          new ui.BoxShadow({
            color: cStyle.c12,
            blurRadius: 1,
            spreadRadius: 2,
          }),
        ],
      }),
      disable: this.editButton.disable,
      onPressed: () => {
        this._onPresseProfileButtonHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: IconView({
        size: 20,
        icon: MaterialIcons.edit,
        color: cStyle.c5,
        className: "x56",
      }),
      onEnter: (event) => {
        this.editButtonOnEnter(event);
      },
      onExit: (event) => {
        this.editButtonOnExit(event);
      },
      className: "xee9 hc",
    });
  }
}
function EditButtonWithState(props: EditButtonWithStateProps) {
  return React.createElement(_EditButtonWithState, props);
}

interface ProviderImageWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  admin: Admin;
}

class ProviderImageState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _ProviderImageWithState extends ObservableComponent<ProviderImageWithStateProps> {
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ProviderImageWithStateProps) {
    super(props);

    this.initState();
  }
  public get admin(): Admin {
    return this.props.admin;
  }
  public get providerImage(): ProviderImageState {
    return this.props.d3eState.providerImage;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("providerImage", null, this.providerImage);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      ["admin", "admin.avatar", "providerImage", "providerImage.hover"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ProviderImageWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public providerImageOnEnter(event): void {
    return this.providerImage.setHover(true);
  }
  public providerImageOnExit(event): void {
    return this.providerImage.setHover(false);
  }
  public dispose(): void {
    this.providerImage.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.ClipRRect({
      borderRadius: ui.BorderRadius.circular(55),
      child: ui.NetworkImage({
        url: this.admin.avatar.downloadUrl,
        width: 110,
        height: 110,
        fit: ui.BoxFit.fill,
        onEnter: (event) => {
          this.providerImageOnEnter(event);
        },
        onExit: (event) => {
          this.providerImageOnExit(event);
        },
        className: "x84 hc vc",
      }),
    });
  }
}
function ProviderImageWithState(props: ProviderImageWithStateProps) {
  return React.createElement(_ProviderImageWithState, props);
}

interface DefaultImageViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
}

class DefaultImageViewState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _DefaultImageViewWithState extends ObservableComponent<DefaultImageViewWithStateProps> {
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DefaultImageViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get defaultImageView(): DefaultImageViewState {
    return this.props.d3eState.defaultImageView;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("defaultImageView", null, this.defaultImageView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["defaultImageView", "defaultImageView.hover"], this.rebuild);
  }
  public defaultImageViewOnEnter(event): void {
    return this.defaultImageView.setHover(true);
  }
  public defaultImageViewOnExit(event): void {
    return this.defaultImageView.setHover(false);
  }
  public dispose(): void {
    this.defaultImageView.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.ClipRRect({
      borderRadius: ui.BorderRadius.circular(55),
      child: ui.AssetImage({
        path: "images/Addperson.png",
        width: 110,
        height: 110,
        onEnter: (event) => {
          this.defaultImageViewOnEnter(event);
        },
        onExit: (event) => {
          this.defaultImageViewOnExit(event);
        },
        className: "xdf hc vc",
      }),
    });
  }
}
function DefaultImageViewWithState(props: DefaultImageViewWithStateProps) {
  return React.createElement(_DefaultImageViewWithState, props);
}

interface ButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSideMenuViewRefs;
  _onPresseProfileButtonHandler?: _EditButtonOnPressed;
  admin: Admin;
}

class ButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _ButtonWithState extends ObservableComponent<ButtonWithStateProps> {
  buttonFocusNode: ui.FocusNode = new ui.FocusNode();
  editButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get admin(): Admin {
    return this.props.admin;
  }
  public get button(): ButtonState {
    return this.props.d3eState.button;
  }
  public get defaultImageView(): DefaultImageViewState {
    return this.props.d3eState.defaultImageView;
  }
  public get editButton(): EditButtonState {
    return this.props.d3eState.editButton;
  }
  public get providerImage(): ProviderImageState {
    return this.props.d3eState.providerImage;
  }
  public get d3eState(): AdminSideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onPresseProfileButtonHandler(): _EditButtonOnPressed {
    return this.props._onPresseProfileButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button", null, this.button);

    this.updateObservable("defaultImageView", null, this.defaultImageView);

    this.updateObservable("providerImage", null, this.providerImage);

    this.updateObservable("editButton", null, this.editButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      [
        "admin",
        "admin.avatar",
        "button",
        "defaultImageView",
        "defaultImageView.hover",
        "editButton",
        "editButton.hover",
        "providerImage",
        "providerImage.hover",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ButtonWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      disable: this.button.disable,
      onFocusChange: (val) => {},
      child: ui.Stack({
        children: [
          ui.Container({
            alignment: ui.Alignment.center,
            child:
              this.admin.avatar === null
                ? DefaultImageViewWithState({ d3eState: this.d3eState })
                : ProviderImageWithState({
                    d3eState: this.d3eState,
                    admin: this.admin,
                  }),
            className: "xc0 hc vc",
            key: "0",
          }),
          this.defaultImageView.hover ||
          this.editButton.hover ||
          this.providerImage.hover
            ? ui.Positioned({
                child: EditButtonWithState({
                  d3eState: this.d3eState,
                  _onPresseProfileButtonHandler:
                    this._onPresseProfileButtonHandler,
                }),
                right: 8,
                bottom: 10,
              })
            : [],
        ],
        className: "x6d4 hc",
      }),
      className: "x477 hc",
    });
  }
}
function ButtonWithState(props: ButtonWithStateProps) {
  return React.createElement(_ButtonWithState, props);
}

class _AdminSideMenuViewState extends ObservableComponent<AdminSideMenuViewProps> {
  static defaultProps = { admin: null, onMenuChanged: null };
  d3eState: AdminSideMenuViewRefs = new AdminSideMenuViewRefs();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminSideMenuViewProps) {
    super(props);

    this.initState();
  }
  public get admin(): Admin {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      ["admin", "admin.email", "admin.mobile", "admin.name"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminSideMenuViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Column({
          children: [
            ButtonWithState({
              d3eState: this.d3eState,
              _onPresseProfileButtonHandler: this.onPresseProfileButtonHandler,
              admin: this.admin,
              key: "0",
            }),
            TextView({
              data: this.admin.name,
              softWrap: true,
              textAlign: ui.TextAlign.center,
              style: new ui.TextStyle({
                fontSize: cStyle.tTextViewHeadlineFiveFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineFiveFontWeightOn,
              }),
              className: "x3d3 hc",
              key: "1",
            }),
            TextView({
              data: "Super Admin",
              softWrap: true,
              textAlign: ui.TextAlign.center,
              style: new ui.TextStyle({
                color: cStyle.c4,
                fontSize: cStyle.tTextViewHeadlineSixFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineSixFontWeightOn,
              }),
              className: "xccd hc",
              key: "2",
            }),
            TextView({
              data: this.admin.email,
              softWrap: true,
              textAlign: ui.TextAlign.center,
              style: new ui.TextStyle({
                fontSize: cStyle.tTextViewHeadlineSixFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineSixFontWeightOn,
              }),
              className: "x212 hc",
              key: "3",
            }),
            TextView({
              data: this.admin.mobile,
              softWrap: true,
              textAlign: ui.TextAlign.center,
              style: new ui.TextStyle({
                fontSize: cStyle.tTextViewHeadlineSixFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineSixFontWeightOn,
              }),
              className: "x861 hc",
              key: "4",
            }),
            Divider({ className: "x2ac hc h", key: "5" }),
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.center,
              children: [
                DashboardButtonWithState({
                  d3eState: this.d3eState,
                  _dashboardButtonHandler: this.dashboardButtonHandler,
                  key: "0",
                }),
                BusinessManagementButtonWithState({
                  d3eState: this.d3eState,
                  _businessManagementButtonHandler:
                    this.businessManagementButtonHandler,
                  key: "1",
                }),
                CategoryManagementButtonWithState({
                  d3eState: this.d3eState,
                  _categoryManagementButtonHandler:
                    this.categoryManagementButtonHandler,
                  key: "2",
                }),
                ConfigurationButtonWithState({
                  d3eState: this.d3eState,
                  _configurationButtonHandler: this.configurationButtonHandler,
                  key: "3",
                }),
              ],
              className: "xa98 hc",
              key: "6",
            }),
          ],
          className: "xa2 hc vc h v",
          key: "0",
        }),
      ],
      className: ui.join(this.props.className, "AdminSideMenuView x85 hc vc v"),
      ...copyBaseUIProps(this.props),
    });
  }
  public dashboardButtonHandler = (d3eState: AdminSideMenuViewRefs): void => {
    this.onMenuChanged("Dashboard");
  };
  public businessManagementButtonHandler = (
    d3eState: AdminSideMenuViewRefs
  ): void => {
    this.onMenuChanged("Business Management");
  };
  public categoryManagementButtonHandler = (
    d3eState: AdminSideMenuViewRefs
  ): void => {
    this.onMenuChanged("Category Management");
  };
  public configurationButtonHandler = (
    d3eState: AdminSideMenuViewRefs
  ): void => {
    this.onMenuChanged("Configuration");
  };
  public onPresseProfileButtonHandler = async (
    d3eState: AdminSideMenuViewRefs
  ): Promise<void> => {
    let fileToUpload: FileToUpload = await Browser.selectFile(
      ["jpg", "jpeg", "png"],
      () => {}
    );

    if (fileToUpload !== null) {
      let res: FileUploadResult = await PlatformClient.upload(fileToUpload);

      if (res.success) {
        this.admin.setAvatar(res.file);

        let result: Result<Admin> = await this.admin.save();
      }
    }
  };
  public get onMenuChanged(): _AdminSideMenuViewOnMenuChanged {
    return this.props.onMenuChanged;
  }
  public get businessManagementButton() {
    return this.d3eState.businessManagementButton;
  }
  public get button() {
    return this.d3eState.button;
  }
  public get categoryManagementButton() {
    return this.d3eState.categoryManagementButton;
  }
  public get configurationButton() {
    return this.d3eState.configurationButton;
  }
  public get dashboardButton() {
    return this.d3eState.dashboardButton;
  }
  public get defaultImageView() {
    return this.d3eState.defaultImageView;
  }
  public get editButton() {
    return this.d3eState.editButton;
  }
  public get providerImage() {
    return this.d3eState.providerImage;
  }
}
export default function AdminSideMenuView(props: AdminSideMenuViewProps) {
  return React.createElement(_AdminSideMenuViewState, {
    ..._AdminSideMenuViewState.defaultProps,
    ...props,
  });
}
